import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    if (this.hasAttribute('manual-load-more')) {
      this.#updateLoadMoreControls();
    } else {
      this.#fetchPage('next');
      this.#fetchPage('previous');
      this.#observeViewMore();
    }

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Loads the next page after an explicit customer action.
   * The triggering element remains a normal link as a no-JavaScript fallback.
   *
   * @param {Event} event
   */
  loadMore = async (event) => {
    const nextPage = this.#getPage('next');

    // Let the link perform a real navigation at the start of each new page window.
    // This caps the rendered grid size while preserving filters, sorting, and a crawlable URL.
    if (nextPage && this.#startsNewLoadMoreWindow(nextPage.page)) return;

    event.preventDefault();

    const button = this.querySelector('[ref="loadMoreButton"]');
    const label = this.querySelector('[ref="loadMoreLabel"]');
    const loadingLabel = this.querySelector('[ref="loadMoreLoadingLabel"]');
    const status = this.querySelector('[ref="loadMoreStatus"]');

    if (!(button instanceof HTMLAnchorElement) || button.getAttribute('aria-busy') === 'true') return;

    const previousCount = this.#getRenderedCards().length;
    button.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-disabled', 'true');

    if (label instanceof HTMLElement) label.hidden = true;
    if (loadingLabel instanceof HTMLElement) loadingLabel.hidden = false;

    try {
      const didLoad = await this.#renderNextPage();
      if (!didLoad) return;

      const loadedCount = this.#getRenderedCards().length - previousCount;
      this.#updateLoadMoreControls();

      if (status instanceof HTMLElement) {
        status.textContent = (status.dataset.successLabel || '').replace('__COUNT__', String(loadedCount));
      }
    } catch (error) {
      if (status instanceof HTMLElement) status.textContent = status.dataset.errorLabel || '';
      console.error(error);
    } finally {
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');

      if (label instanceof HTMLElement) label.hidden = false;
      if (loadingLabel instanceof HTMLElement) loadingLabel.hidden = true;
    }
  };

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } finally {
      resolvePromise();
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return false;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return false;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      await this.#fetchPage('next');
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return false;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      if (!this.hasAttribute('manual-load-more')) this.#fetchPage('next');
    });

    return true;
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const cards = this.#getRenderedCards();
    const isPrevious = type === 'previous';

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * Returns the product cards currently rendered in the grid.
   * Querying the grid directly keeps the result accurate immediately after appending a page.
   *
   * @returns {HTMLElement[]}
   */
  #getRenderedCards() {
    const { grid } = this.refs;
    if (!grid) return [];

    return Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]')).filter(
      (card) => card instanceof HTMLElement
    );
  }

  /**
   * @param {number} pageNumber
   * @returns {boolean}
   */
  #startsNewLoadMoreWindow(pageNumber) {
    const pageLimit = Number(this.getAttribute('manual-load-more-pages') || 4);
    if (!Number.isFinite(pageLimit) || pageLimit < 1) return false;

    return pageNumber > 1 && (pageNumber - 1) % pageLimit === 0;
  }

  #updateLoadMoreControls() {
    const control = this.querySelector('[ref="loadMoreControl"]');
    if (!(control instanceof HTMLElement)) return;

    const renderedCards = this.#getRenderedCards();
    const renderedCount = renderedCards.length;
    const totalProducts = Number(control.dataset.totalProducts || renderedCount);
    const batchSize = Number(control.dataset.batchSize || renderedCount);
    const renderedPages = renderedCards
      .map((card) => Number(card.dataset.page))
      .filter((page) => Number.isFinite(page) && page > 0);
    const firstRenderedPage = renderedPages.length ? Math.min(...renderedPages) : 1;
    const firstRenderedProduct = (firstRenderedPage - 1) * batchSize + 1;
    const lastRenderedProduct = Math.min(firstRenderedProduct + renderedCount - 1, totalProducts);
    const remainingProducts = Math.max(totalProducts - lastRenderedProduct, 0);
    const nextBatchSize = Math.min(batchSize, remainingProducts);
    const locale = document.documentElement.lang || 'en';
    const numberFormatter = new Intl.NumberFormat(locale);
    const count = control.querySelector('[ref="loadMoreCount"]');
    const button = control.querySelector('[ref="loadMoreButton"]');
    const label = control.querySelector('[ref="loadMoreLabel"]');
    const complete = control.querySelector('[ref="loadMoreComplete"]');
    const nextPage = this.#getPage('next');
    const canLoadMore = remainingProducts > 0 && this.#shouldUsePage(nextPage);

    if (count instanceof HTMLElement) {
      count.textContent = (control.dataset.countTemplate || '')
        .replace('__START__', numberFormatter.format(firstRenderedProduct))
        .replace('__END__', numberFormatter.format(lastRenderedProduct))
        .replace('__TOTAL__', numberFormatter.format(totalProducts));
    }

    if (button instanceof HTMLAnchorElement) {
      button.hidden = !canLoadMore;
      if (canLoadMore && nextPage) {
        const buttonUrl = new URL(nextPage.url);
        if (this.#startsNewLoadMoreWindow(nextPage.page)) buttonUrl.hash = 'ResultsList';
        button.href = buttonUrl.toString();
      }
    }

    if (label instanceof HTMLElement) {
      label.textContent = (control.dataset.buttonTemplate || '').replace(
        '__COUNT__',
        numberFormatter.format(nextBatchSize)
      );
    }

    if (complete instanceof HTMLElement) complete.hidden = canLoadMore;
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      if (this.hasAttribute('manual-load-more')) {
        observer.disconnect();
        this.#updateLoadMoreControls();
        return;
      }

      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }

        if (this.hasAttribute('manual-load-more')) this.#updateLoadMoreControls();
      }, 3000);
    }
  };
}
